import { useState } from 'react'

import { taskRoute } from '@/app/routes'
import { useDocuments, useRoadmap, useTasks } from '@/shared/api/queries'
import type { Document, Evaluation, Task } from '@/shared/api/queries'
import { useDeadlineText } from '@/shared/format'
import { useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import {
  areaIcon,
  areaLabel,
  areaOf,
  byUrgency,
  documentTypeLabel,
  evaluationsOf,
  requirementOf,
  requiredDocumentsOf,
  urgencyOf,
} from '@/shared/model'
import type { Urgency } from '@/shared/model'
import { Badge, Card, Chip, Empty, Skeleton, Stack, StateBlock, Text } from '@/shared/ui'

const FILTERS = ['all', 'today', 'upcoming', 'overdue', 'done'] as const
const THIS_WEEK_DAYS = 7
const MAX_TAGS = 2

type Filter = (typeof FILTERS)[number]

function filterLabel(filter: Filter): TranslationKey {
  return `tasks.filter.${filter}`
}

function urgencyOfTask(task: Task): Urgency {
  return task.status === 'open' ? urgencyOf(task) : 'done'
}

function daysLeft(task: Task): number {
  if (!task.deadline) {
    return Number.POSITIVE_INFINITY
  }

  const day = 24 * 60 * 60 * 1000

  return Math.ceil((new Date(task.deadline).getTime() - Date.now()) / day)
}

function keeps(task: Task, filter: Filter): boolean {
  if (filter === 'done') {
    return task.status !== 'open'
  }

  if (task.status !== 'open') {
    return false
  }

  if (filter === 'today') {
    return daysLeft(task) === 0
  }

  if (filter === 'upcoming') {
    return daysLeft(task) > 0
  }

  if (filter === 'overdue') {
    return daysLeft(task) < 0
  }

  return true
}

function TaskCard({
  task,
  evaluations,
  documents,
}: {
  task: Task
  evaluations: Evaluation[]
  documents: Document[]
}) {
  const t = useTranslate()
  const deadline = useDeadlineText()

  const area = areaOf(task, evaluations) ?? 'other'
  const urgency = urgencyOfTask(task)
  const needed = requiredDocumentsOf(requirementOf(task, evaluations), documents).slice(0, MAX_TAGS)

  return (
    <li>
      <Card to={taskRoute(task.id)} className={`tone--${urgency}`}>
        <Stack direction="row" align="start" gap="sm">
          <span
            className={task.status === 'open' ? 'check' : 'check check--done'}
            aria-hidden="true"
          >
            {task.status === 'open' ? '' : '✓'}
          </span>
          <Stack gap="sm">
            <Text as="span" role="body-strong">
              {task.title}
            </Text>
            <Text as="span" role="label" tone="muted">
              {areaIcon(area)} {t(areaLabel(area))} ·{' '}
              <Text as="span" role="label" tone="tone">
                {deadline(task.deadline) ?? t('tasks.no_deadline')}
              </Text>
            </Text>
            {needed.length > 0 ? (
              <Stack direction="row" gap="xs" wrap>
                {needed.map((item) => (
                  <Badge key={item.type} {...(item.document ? { status: 'success' as const } : {})}>
                    {item.document ? '✓ ' : ''}
                    {t(documentTypeLabel(item.type))}
                  </Badge>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </Card>
    </li>
  )
}

export function TasksPage() {
  const t = useTranslate()
  const [filter, setFilter] = useState<Filter>('all')

  const tasks = useTasks()
  const roadmap = useRoadmap()
  const documents = useDocuments()

  const evaluations = evaluationsOf(roadmap.data)
  const all = tasks.data ?? []

  const open = all.filter((task) => task.status === 'open')
  const soon = open.filter((task) => daysLeft(task) <= THIS_WEEK_DAYS).length

  const shown = all
    .filter((task) => keeps(task, filter))
    .sort((a, b) => byUrgency(urgencyOf(a), urgencyOf(b)))

  return (
    <Stack gap="md">
      <Stack as="header" gap="xs">
        <Text as="h1" role="heading-2">
          {t('tasks.title')}
        </Text>
        <Text role="label" tone="muted">
          {soon > 0
            ? t('tasks.summary', { open: open.length, soon })
            : t('tasks.summary.clear', { open: open.length })}
        </Text>
      </Stack>

      <Stack direction="row" gap="sm" wrap>
        {FILTERS.map((candidate) => (
          <Chip
            key={candidate}
            selected={filter === candidate}
            onClick={() => setFilter(candidate)}
          >
            {t(filterLabel(candidate))}
          </Chip>
        ))}
      </Stack>

      <StateBlock
        pending={tasks.isPending}
        error={tasks.error}
        onRetry={() => void tasks.refetch()}
        skeleton={<Skeleton variant="card" rows={3} />}
      >
        {shown.length === 0 ? (
          <Empty icon="🎉" title={t('tasks.empty.title')} body={t('tasks.empty.filter')} />
        ) : (
          <Stack as="ul" gap="sm">
            {shown.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                evaluations={evaluations}
                documents={documents.data ?? []}
              />
            ))}
          </Stack>
        )}
      </StateBlock>
    </Stack>
  )
}
